'use client';

import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Autoplay, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { useRouter } from 'next/navigation';
import fetchContents from '@/redux/thunk/contents-thunk';
import { bannerTypes, mediaTypes } from '@/constants/types';
import Button from '@/app/components/Button';
import Text from '@/app/components/Text';
import YoutubeIframe from '@/app/components/YoutubeIframe';
import BannerImageComponent from '@/app/components/Image/BannerImageComponent';
import BannerSkeleton from '../Skeleton/BannerSkeleton';
import VideoPlayer from '../VideoPlayer';

// swiper slider
import 'swiper/css';
import 'swiper/css/pagination';

function HomepageHeroBannerCarousel({ section }) {
  const router = useRouter();
  const dispatch = useDispatch();
  const [data, setData] = useState([]);
  const { isLoading } = useSelector((state) => state.sections);
  const { contents } = useSelector((state) => state.contents);
  const { lang: selectLang } = useSelector((state) => state.globalSettings);
  const [loading, setLoading] = useState(true);
  const [isFirst, setIsFirst] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [swiperInstance, setSwiperInstance] = useState(null);

  // youtube embed video
  const onPlayerStateChange = (event) => {
    if (!swiperInstance) return;

    if (event.data === window.YT.PlayerState.PLAYING) {
      setIsPaused(true);
    } else if (
      event.data === window.YT.PlayerState.PAUSED ||
      event.data === window.YT.PlayerState.ENDED
    ) {
      setIsPaused(false);
    }
  };

  useEffect(() => {
    if (swiperInstance) {
      if (isPaused) {
        swiperInstance.autoplay.stop();
      } else {
        swiperInstance.autoplay.start();
      }
    }
  }, [isPaused, swiperInstance]);

  useEffect(() => {
    dispatch(
      fetchContents({
        sectionId: section?.id,
        contentType: section?.content_type,
      }),
    );
  }, []);

  useEffect(() => {
    if (contents?.length > 0) {
      try {
        const parsedItems =
          typeof contents === 'string' ? JSON.parse(contents) : contents;
        setData(parsedItems);
      } catch (error) {
        // console.error('Error parsing contents:', error);
      }
    }
  }, [contents]);

  const handleUserInteraction = (interaction) => {
    setIsFirst(true);
    if (isFirst) {
      setIsPaused(interaction);
    }
  };

  const handleClickUrl = (url) => {
    if (!url?.en?.url) return null;

    if (url?.en?.type === 'web') {
      router.push(url?.en?.url);
    } else if (url?.en?.type === 'external') {
      window.open(url?.en?.url, '_blank');
    } else if (url?.en?.url) {
      router.push(url?.en?.url);
    }
  };

  // youtube embed video
  const extractYouTubeId = (url) => {
    const match = url.match(
      /(?:youtube\.com\/(?:[^/]+\/[^/]+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/s]{11})/,
    );
    return match ? match[1] : null;
  };

  const renderContent = (item) => {
    if (item?.type === mediaTypes.image) {
      return (
        <BannerImageComponent
          mobileSrc={item?.image?.small}
          desktopSrc={item?.image?.large}
          alt={item?.title?.en}
          type={bannerTypes.homePageHero}
        />
      );
    }

    if (item?.type === mediaTypes.youtubeVideo) {
      const videoId = extractYouTubeId(item?.video);
      return (
        <div className="max-w-full w-full aspect-[1.040/1] md:aspect-[4.114/1] max-h-max">
          <YoutubeIframe
            playWithID
            videoId={videoId}
            onStateChange={onPlayerStateChange}
            className="w-full object-center object-contain max-w-full aspect-[1.040/1] md:aspect-[4.114/1] max-h-max"
          />
        </div>
      );
    }

    return (
      <VideoPlayer
        videoLink={item?.video}
        onUserInteraction={handleUserInteraction}
        className="max-w-full w-full aspect-[1.040/1] md:aspect-[4.114/1] max-h-max"
        smallDeviceRatio="1.040 / 1"
        largeDeviceRatio="4.114 / 1"
        height="100%"
      />
    );
  };

  useEffect(() => {
    const loadingTimeout = setTimeout(() => {
      setLoading(false);
    }, 2000); // 2sec delay to simulate loading

    return () => clearTimeout(loadingTimeout);
  }, []);

  if (loading || isLoading) {
    return (
      <div className="max-w-full w-full h-full mb-16 lg:mb-[42px]">
        <BannerSkeleton
          className="aspect-[1.040/1] md:aspect-[4.114/1] max-h-max"
          showNextPrevBtnSkeleton={false}
          showSliderButtons={false}
        />
      </div>
    );
  }

  return (
    <Swiper
      onSwiper={setSwiperInstance}
      modules={[Autoplay, Pagination]}
      slidesPerView={1}
      autoplay={{
        delay: 3000,
        disableOnInteraction: false,
        pauseOnMouseEnter: true,
      }}
      speed={800}
      mousewheel
      pagination={{ clickable: true }}
      className="hero-banner mb-16 lg:mb-[40px] group cursor-pointer"
    >
      {data?.map((item, index) => (
        <SwiperSlide key={item?.id ?? index}>
          <div
            aria-label={`Go to ${item?.redirect_url[selectLang]?.text ?? 'Learn more'}`}
            role="button"
            tabIndex={0}
            onClick={() => handleClickUrl(item?.redirect_url)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleClickUrl(item?.redirect_url?.en?.url);
              }
            }}
            className="aspect-[1.040/1] md:aspect-[4.114/1] relative overflow-hidden"
          >
            {renderContent(item)}
            {item?.redirect_url[selectLang]?.url ||
            item?.redirect_url?.en?.url ? (
              <Button
                type="outline"
                className="hidden group-hover:flex w-fit !h-[24px] lg:!h-[40px] mx-auto !py-4 lg:!py-10 !px-16 !bg-white border !border-himalayan-poppy absolute bottom-[35px] md:bottom-[8px] left-0 right-0 md:right-[50px] md:left-auto rounded-[100px] transition-all duration-500"
              >
                <Text
                  type="paragraph"
                  className="text-xs lg:text-base text-senate font-semibold"
                >
                  {item?.redirect_url[selectLang]?.text ?? 'Learn more'}
                </Text>
              </Button>
            ) : null}
          </div>
        </SwiperSlide>
      ))}
    </Swiper>
  );
}

export default HomepageHeroBannerCarousel;
